import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { useCallback, useEffect, useRef, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { FaArrowLeft } from 'react-icons/fa';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../Layout/Header";
import { getEndpointUrl, getTimestamp } from "../../Utils/Common";
import AddFormElementDialog from "./AddFormElementDialog";
import { SplitButton } from 'primereact/splitbutton';
import UpdateFormElementDialog from "./UpdateFormElementDialog";
import DeleteFormElementDialog from "./DeleteFormElementDialog";

function DetailSection() {
    const { section_id, element_id } = useParams();
    const [isLoading, setLoading] = useState(true);
    const [accesToken] = useState(JSON.parse(localStorage.getItem('credentials')) || []);
    const [sectionTitle, setSectionTitle] = useState('');
    const [elements, setElements] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const inputRef = useRef();
    // State untuk memanggil dialog update
    const [showUpdate, setShowUpdate] = useState(false);
    const [updateData, setUpdateData] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const navigate = useNavigate();

    const [userLogin, setUserLogin] = useState(
        JSON.parse(localStorage.getItem('knowLogin')) || []
    );

    const fetchSectionDetail = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `${getEndpointUrl()}/v2/form_sections/${section_id}?v=${getTimestamp()}`,
                {
                    headers: new Headers({
                        Authorization: `Bearer ${accesToken}`,
                    }),
                }
            );
            const json = await res.json();
            if (json.status === 'ok' && json.results) {
                setSectionTitle(json.results.title || '');
                setElements(json.results.form_elements || []);
            } else {
                setSectionTitle('');
                setElements([]);
            }
        } catch (err) {
            setSectionTitle('');
            setElements([]);
        } finally {
            setLoading(false);
        }
    }, [section_id, accesToken]);

    useEffect(() => {
        fetchSectionDetail();
    }, [fetchSectionDetail]);

    const onGlobalFilterChange = (e) => {
        setSearchTerm(e.target.value || '');
    };

    const filteredElements = (elements || []).filter((el) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        const name = (el?.name || '').toLowerCase();
        const desc = (el?.html_description || '').toLowerCase();
        return name.includes(term) || desc.includes(term);
    });

    const header = (
        <Row>
            <Row>
                <div className="my-2">
                    <Link to="/form-options" className="btn btn-outline-secondary my-1">
                        <FaArrowLeft /> Kembali
                    </Link>
                </div>
            </Row>
            <Row>
                <Col md={8} sm={12} className="my-2 flex justify-content-between">
                    <h5 className="me-auto fw-bold pt-2">
                        Form Elements — {sectionTitle || `Section #${sectionTitle}`}
                    </h5>
                </Col>
                <Col md={4} sm={12} className="my-2 d-flex align-items-center justify-content-end gap-2">
                    <div className="p-input-icon-left" style={{ minWidth: '280px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            ref={inputRef}
                            className="p-inputtext-sm"
                            onChange={onGlobalFilterChange}
                            placeholder="Pencarian nama/deskripsi..."
                            style={{ minWidth: '100%' }}
                        />
                    </div>
                    <Button
                        className="btn btn-secondary"
                        onClick={() => {
                            setShowCreate(true);
                        }}>
                        Tambah
                    </Button>
                </Col>
            </Row>
        </Row>
    );

    const booleanTemplate = (val) => (val ? 'Ya' : 'Tidak');

    const groupsCountTemplate = (rowData) => (
        <NumericFormat
            value={(rowData?.form_choice_groups || []).length}
            displayType={'text'}
            thousandSeparator={true}
            className="bg-transparent"
            renderText={(value, props) => <span {...props}>{value}</span>}
        />
    );

    const detailsCountTemplate = (rowData) => {
        const total = (rowData?.form_choice_groups || []).reduce((sum, grp) => {
            return sum + ((grp?.form_choices_details || []).length);
        }, 0);
        return (
            <NumericFormat
                value={total}
                displayType={'text'}
                thousandSeparator={true}
                className="bg-transparent"
                renderText={(value, props) => <span {...props}>{value}</span>}
            />
        );
    };

    const kelolaBodyTemplate = (rowData) => {
        return <SplitButton size="small" label="Lihat detil" icon="pi pi-eye"
            onClick={(e) => {
                navigate(`/form-options/${section_id}/${rowData.id}`);
            }}
            model={[
                {
                    label: 'Edit',
                    icon: 'pi pi-pencil',
                    disabled: userLogin.role != 'admin',
                    command: () => {
                        setUpdateData(rowData);
                        setShowUpdate(true);
                    }
                },
                {
                    label: 'Hapus',
                    icon: 'pi pi-trash',
                    disabled: userLogin.role != 'admin',
                    command: () => {
                        setDeleteData(rowData);
                        setShowDelete(true);
                    }
                }
            ]} severity="secondary" text />
    };

    return (
        <>
            <Helmet>
                <title>Form Elements — {sectionTitle || section_id}</title>
            </Helmet>
            <Header />
            <Container className="mt-3">
                <DataTable
                    value={filteredElements}
                    size="small"
                    responsiveLayout="scroll"
                    dataKey="id"
                    header={header}
                    emptyMessage="Tidak ada data yang ditemukan."
                    paginator
                    rows={15}
                    loading={isLoading}
                >
                    <Column field="id" header="ID" style={{ width: '90px' }} />
                    <Column field="name" header="NAMA" />
                    <Column field="html_description" header="DESKRIPSI" />
                    <Column header="REQUIRED" body={(rowData) => booleanTemplate(rowData?.required)} style={{ width: '120px' }} />
                    <Column header="NOTE" body={(rowData) => booleanTemplate(rowData?.note)} style={{ width: '100px' }} />
                    <Column header="JUMLAH PILIHAN" body={groupsCountTemplate} style={{ width: '140px' }} />
                    {/* Kolom aksi untuk membuka dialog update */}
                    <Column header="KELOLA" body={kelolaBodyTemplate} />
                </DataTable>
            </Container>

            {/* Dialog Update */}
            <UpdateFormElementDialog
                visible={showUpdate}
                onHide={() => setShowUpdate(false)}
                element={updateData}
                formSectionId={parseInt(section_id, 10)}
                onSuccess={(updated) => {
                    // Replace item di list elements
                    setElements((prev) => prev.map((el) => (el.id === updated.id ? updated : el)));
                }}
            />
            {/* Dialog Create */}
            <AddFormElementDialog
                visible={showCreate}
                onHide={() => setShowCreate(false)}
                formSectionId={parseInt(section_id, 10)}
                onSuccess={(created) => {
                    // Tambahkan element baru ke list
                    setElements((prev) => [created, ...prev]);
                }}
            />
            {/* Dialog Delete */}
            <DeleteFormElementDialog
                visible={showDelete}
                onHide={() => setShowDelete(false)}
                element={deleteData}
                onSuccess={(deletedId) => {
                    setElements((prev) => prev.filter((el) => el.id !== deletedId));
                }}
            />
        </>
    );
};

export default DetailSection;